import { NavigateOptions, useLocation } from "@reach/router"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { getWindow } from "../utils/mock-window"
import { navigate } from "gatsby"

type Params = { [key: string]: any }

const setQueryToCurrentUrl = (params: Params) => {
    const url = new URL(getWindow().location.href)

    Object.keys(params).forEach(key => {
        const value = params[key]
        if (value === null || value === undefined) {
            return url.searchParams.delete(key)
        }
        return url.searchParams.set(key, typeof value === "object" ? JSON.stringify(value) : value)
    })
    return url
}

// TODO: deprecated, remove hook when removed from codebase
const useUrlSearchParams = () => {
    const [, forceUpdate] = useState<undefined | {}>()

    const locationSearch = getWindow().location.search

    const urlSearchParams = useMemo(() => {
        return new URLSearchParams(locationSearch)
    }, [locationSearch])

    const params = useMemo(() => {
        let params: Params = {}
        for (let item of urlSearchParams) {
            try {
                params[item[0]] = JSON.parse(item[1])
            } catch {
                params[item[0]] = item[1]
            }
        }
        return params
    }, [urlSearchParams])

    const redirectToNewSearchParams = useCallback((params: Params) => {
        const url = setQueryToCurrentUrl(params)

        if (getWindow().location.search !== url.search) {
            getWindow().history.replaceState({}, "", url)
        }
        if (urlSearchParams.toString() !== url.searchParams.toString()) {
            forceUpdate({})
        }
    }, [urlSearchParams])

    useEffect(() => {
        redirectToNewSearchParams(params)
    }, [params, redirectToNewSearchParams])

    const setParams = (params: Params) => {
        redirectToNewSearchParams(params)
    }

    useEffect(() => {
        const onPopState = () => {
            forceUpdate({})
        }
        getWindow().addEventListener("popstate", onPopState)
        return () => {
            getWindow().removeEventListener("popstate", onPopState)
        }
    }, [])

    return [params, setParams] as const
}

export { useUrlSearchParams }

function getSearchParamsForLocation(locationSearch: string, defaultSearchParams: URLSearchParams | null) {
    let searchParams = createSearchParams(locationSearch)

    if (defaultSearchParams) {
        // Use `defaultSearchParams.forEach(...)` here instead of iterating of
        // `defaultSearchParams.keys()` to work-around a bug in Firefox related to
        // web extensions. Relevant Bugzilla tickets:
        // https://bugzilla.mozilla.org/show_bug.cgi?id=1414602
        // https://bugzilla.mozilla.org/show_bug.cgi?id=1023984
        defaultSearchParams.forEach((_, key) => {
            if (!searchParams.has(key)) {
                defaultSearchParams.getAll(key).forEach(value => {
                    searchParams.append(key, value)
                })
            }
        })
    }

    return searchParams
}
type ParamKeyValuePair = [string, string]

type URLSearchParamsInit = string | ParamKeyValuePair[] | Record<string, string | string[]> | URLSearchParams

type SetURLSearchParams = (
    nextInit?: URLSearchParamsInit | ((prev: URLSearchParams) => URLSearchParamsInit),
    navigateOpts?: NavigateOptions<{}>
) => void

function createSearchParams(init: URLSearchParamsInit = ""): URLSearchParams {
    return new URLSearchParams(
        typeof init === "string" || Array.isArray(init) || init instanceof URLSearchParams
            ? init
            : Object.keys(init).reduce((memo, key) => {
                  let value = init[key]
                  return memo.concat(Array.isArray(value) ? value.map(v => [key, v]) : [[key, value]])
              }, [] as ParamKeyValuePair[])
    )
}

export function useSearchParams(defaultInit?: URLSearchParamsInit): [URLSearchParams, SetURLSearchParams] {
    let defaultSearchParamsRef = useRef(createSearchParams(defaultInit))
    let hasSetSearchParamsRef = useRef(false)

    let location = useLocation()
    let searchParams = useMemo(
        () =>
            // Only merge in the defaults if we haven't yet called setSearchParams.
            // Once we call that we want those to take precedence, otherwise you can't
            // remove a param with setSearchParams({}) if it has an initial value
            getSearchParamsForLocation(
                location.search,
                hasSetSearchParamsRef.current ? null : defaultSearchParamsRef.current
            ),
        [location.search]
    )

    let setSearchParams = useCallback<SetURLSearchParams>(
        (nextInit, navigateOptions) => {
            const newSearchParams = createSearchParams(
                typeof nextInit === "function" ? nextInit(searchParams) : nextInit
            )
            hasSetSearchParamsRef.current = true
            navigate("?" + newSearchParams, navigateOptions)
        },
        [searchParams]
    )

    return [searchParams, setSearchParams]
}

export function useQueryParams() {
    const location = useLocation()
    const [searchParams, setSearchParams] = useSearchParams()

    const getQueryParams = useMemo(() => {
        return Object.fromEntries(searchParams)
    }, [searchParams])

    const getQueryParam = useCallback(
        (key: string) => {
            const params = new URLSearchParams(location.search)
            return params.get(key) || ""
        },
        [location.search]
    )

    const setQueryParam = useCallback(
        (key: string, value: string, navigateOptions?: NavigateOptions<{}>) => {
            const params = new URLSearchParams(location.search)
            params.set(key, value.toString())
            setSearchParams(params.toString(), navigateOptions)
        },
        [location.search, setSearchParams]
    )

    const setQueryParams = useCallback(
        (newParams: { key: string; value: string }[], navigateOptions?: NavigateOptions<{}>) => {
            const params = new URLSearchParams(location.search)
            newParams.forEach(p => params.set(p.key, p.value.toString()))
            setSearchParams(params.toString(), navigateOptions)
        },
        [location.search, setSearchParams]
    )

    const removeQueryParam = useCallback(
        (key: string, navigateOptions?: NavigateOptions<{}>) => {
            const params = new URLSearchParams(location.search)
            params.delete(key)
            setSearchParams(params.toString(), navigateOptions)
        },
        [location.search, setSearchParams]
    )

    const removeQueryParams = useCallback(
        (keys: string[], navigateOptions?: NavigateOptions<{}>) => {
            const params = new URLSearchParams(location.search)
            keys.forEach(key => params.delete(key))
            setSearchParams(params.toString(), navigateOptions)
        },
        [location.search, setSearchParams]
    )

    return {
        getQueryParams,
        getQueryParam,
        setQueryParam,
        setQueryParams,
        removeQueryParam,
        removeQueryParams,
    }
}
